import { type ChakraProps, type CSSObject, type LayoutProps } from "@chakra-ui/react";
import { type BrandColor, type Theme } from "contexts/color-theme";
import { type CaseStudyIndustry, type Customer } from "lib/sanity";
import { type CSSProperties } from "react";

/**
 * IMPORTANT:
 * When using this style, apply className="group" to the parent element
 * and use the `_groupHover` prop for card hover styles.
 *
 * This parent wrapper prevents layout shift when hovering near the card's bottom edge
 * where the y-translation hover effect is applied.
 *
 * For more details: https://github.com/hightouchio/site/pull/1340
 */
export const cardHoverTranslation: CSSObject = {
  transform: { base: "none", md: "translateY(-8px)" },
};

export const capitalizeFirstLetter: ChakraProps["sx"] = {
  "&::first-letter": { textTransform: "capitalize" },
};

/** Generates styles for customer logos in dark themes. **/
export function getCustomerLogoStyles(theme: Theme):
  | {
      filter: string;
      opacity: number;
    }
  | undefined {
  return theme === "dark" ? { filter: "brightness(0) invert(1)", opacity: 0.9 } : undefined;
}

/** Returns the URL of the customer logo to display based on the current theme. */
export function getCustomerLogoUrl(customer: Pick<Customer, "logoUrl" | "logoMonoUrl">, theme: Theme): string {
  return theme === "dark" ? customer.logoMonoUrl ?? customer.logoUrl : customer.logoUrl;
}

/**
 * Set display value so the element only renders on `sm` screens or smaller.
 * Default `display` value for showing the element is `block`.
 * */
export function mobileOnlyDisplay(display: CSSProperties["display"] = "block"): LayoutProps["display"] {
  return { base: display, md: "none" };
}

/**
 * Set display value so the element only renders on `md` screens or larger.
 * Default `display` value for showing the element is `block`.
 * */
export function desktopOnlyDisplay(display: CSSProperties["display"] = "block"): LayoutProps["display"] {
  return { base: "none", md: display };
}

export function getCaseStudyIndustryColor(industrySlug: CaseStudyIndustry["slug"]): BrandColor {
  switch (industrySlug) {
    case "b2b": {
      return "blue";
    }

    case "fintech": {
      return "green";
    }

    case "media": {
      return "violet";
    }

    case "retail": {
      return "yellow";
    }

    default: {
      return "gray";
    }
  }
}
